import { c as _c } from "react/compiler-runtime";
import { getOrgOnboardingsById, getRequestClient, postOrgOnboardings } from '@onefootprint/fetch-dashboard';
import { onboarding } from '@onefootprint/footprint-js';
import { IcoWarning16 } from '@onefootprint/icons';
import type { ApiError, CreateOnboardingResponse, PlaybookVersionNoGraph, PostOrgOnboardingsData, VaultKind } from '@onefootprint/request-types/dashboard';
import { Button, Tooltip } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import type { CreateOnboardingBody, RunFormValues } from '../onboarding.types';
import { buildOnboardingBody } from '../onboarding.utils';
import { ApiErrorAlert } from './api-error-alert';
import { RunResult } from './run-result';
type RunActionsProps = {
  savedFpId: string | null;
  entityKind: VaultKind;
  playbook: PlaybookVersionNoGraph;
  hasErrors: boolean;
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
  hasPendingChanges: boolean;
  versionId?: string;
  onClose: () => void;
};
type LastRun = {
  fpId: string;
  onboardingExternalId?: string;
};
export const RunActions = t0 => {
  const $ = _c(70);
  const {
    savedFpId,
    entityKind,
    playbook,
    hasErrors,
    hasHiddenErrors,
    onRevealHiddenErrors,
    hasPendingChanges,
    versionId,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    authHeaders,
    isLive
  } = useSession();
  const form = useFormContext();
  const [lastRun, setLastRun] = useState(null);
  let t2;
  if ($[1] !== authHeaders) {
    t2 = {
      mutationFn: async id => {
        const {
          data
        } = await getOrgOnboardingsById({
          path: {
            id
          },
          headers: authHeaders
        });
        return data;
      }
    };
    $[1] = authHeaders;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const refetchMutation = useMutation(t2);
  let t3;
  if ($[3] !== authHeaders || $[4] !== refetchMutation) {
    t3 = {
      mutationFn: async body => {
        refetchMutation.reset();
        const client = getRequestClient({
          disableBodySnakeCaseConversion: true
        });
        const {
          data: data_0
        } = await postOrgOnboardings({
          body: body as unknown as PostOrgOnboardingsData['body'],
          client,
          headers: authHeaders
        });
        if (data_0.status === "incomplete" && data_0.continueOnboarding) {
          await onboarding.initialize({
            onboardingSessionToken: data_0.continueOnboarding.token,
            onComplete: () => refetchMutation.mutate(data_0.id)
          });
        }
        return data_0;
      }
    };
    $[3] = authHeaders;
    $[4] = refetchMutation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const runMutation = useMutation(t3);
  let t4;
  if ($[6] !== form || $[7] !== hasPendingChanges || $[8] !== isLive || $[9] !== playbook || $[10] !== runMutation || $[11] !== savedFpId || $[12] !== t || $[13] !== versionId) {
    t4 = values => {
      if (!savedFpId) {
        return;
      }
      let body_0;
      try {
        body_0 = buildOnboardingBody({
          fpId: savedFpId,
          playbook,
          values,
          isLive,
          hasPendingChanges,
          versionId,
          now: new Date().toISOString()
        });
      } catch {
        form.setError("prerequisiteData", {
          message: t(_temp)
        });
        return;
      }
      runMutation.mutate(body_0, {
        onSettled: () => setLastRun({
          fpId: savedFpId,
          onboardingExternalId: values.onboardingExternalId
        })
      });
    };
    $[6] = form;
    $[7] = hasPendingChanges;
    $[8] = isLive;
    $[9] = playbook;
    $[10] = runMutation;
    $[11] = savedFpId;
    $[12] = t;
    $[13] = versionId;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleSubmit = t4;
  let t5;
  if ($[15] !== entityKind || $[16] !== hasErrors || $[17] !== hasHiddenErrors || $[18] !== savedFpId || $[19] !== t) {
    t5 = hasErrors && !hasHiddenErrors ? t(_temp2) : savedFpId ? undefined : t($_1 => $_1.runMode.run.needEntityHint[entityKind]);
    $[15] = entityKind;
    $[16] = hasErrors;
    $[17] = hasHiddenErrors;
    $[18] = savedFpId;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const disabledTooltip = t5;
  const isRunning = runMutation.isPending;
  let t6;
  if ($[21] !== form.control) {
    t6 = {
      control: form.control,
      name: "onboardingExternalId"
    };
    $[21] = form.control;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const obExternalId = useWatch(t6);
  let t7;
  if ($[23] !== refetchMutation || $[24] !== runMutation) {
    t7 = () => {
      runMutation.reset();
      refetchMutation.reset();
    };
    $[23] = refetchMutation;
    $[24] = runMutation;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== savedFpId) {
    t8 = [savedFpId];
    $[26] = savedFpId;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  useEffect(t7, t8);
  const isReusingFpId = !!savedFpId && savedFpId === lastRun?.fpId;
  const showIdempotencyWarning = isReusingFpId && !!lastRun && (!obExternalId || lastRun.onboardingExternalId === obExternalId);
  const error = runMutation.error ?? refetchMutation.error;
  let t9;
  if ($[28] !== runMutation.error?.code || $[29] !== t) {
    t9 = runMutation.error?.code === "T122" ? t(_temp3) : undefined;
    $[28] = runMutation.error?.code;
    $[29] = t;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const errorHint = t9;
  const result = refetchMutation.data ?? runMutation.data;
  let t10;
  if ($[31] !== entityKind || $[32] !== error || $[33] !== errorHint || $[34] !== lastRun || $[35] !== result) {
    t10 = (!!error || result) && <>{!!error && <ApiErrorAlert error={error} hint={errorHint} />}{result && lastRun && <RunResult run={result} fpId={lastRun.fpId} entityKind={entityKind} />}</>;
    $[31] = entityKind;
    $[32] = error;
    $[33] = errorHint;
    $[34] = lastRun;
    $[35] = result;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t) {
    t11 = t(_temp4);
    $[37] = t;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== onClose || $[40] !== t11) {
    t12 = <Button variant="secondary" onClick={onClose}>{t11}</Button>;
    $[39] = onClose;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== showIdempotencyWarning || $[43] !== t) {
    t13 = showIdempotencyWarning ? <Tooltip content={t(_temp5)}><IcoWarning16 color="warning" /></Tooltip> : null;
    $[42] = showIdempotencyWarning;
    $[43] = t;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const t14 = !disabledTooltip;
  let t15;
  if ($[45] !== form || $[46] !== handleSubmit || $[47] !== hasHiddenErrors || $[48] !== onRevealHiddenErrors) {
    t15 = () => hasHiddenErrors ? onRevealHiddenErrors() : void form.handleSubmit(handleSubmit)();
    $[45] = form;
    $[46] = handleSubmit;
    $[47] = hasHiddenErrors;
    $[48] = onRevealHiddenErrors;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const t16 = isRunning || !!disabledTooltip;
  let t17;
  if ($[50] !== t) {
    t17 = t(_temp6);
    $[50] = t;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== isRunning || $[53] !== t15 || $[54] !== t16 || $[55] !== t17) {
    t18 = <Button onClick={t15} loading={isRunning} disabled={t16}>{t17}</Button>;
    $[52] = isRunning;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== disabledTooltip || $[58] !== t14 || $[59] !== t18) {
    t19 = <Tooltip disabled={t14} content={disabledTooltip}>{t18}</Tooltip>;
    $[57] = disabledTooltip;
    $[58] = t14;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== t13 || $[62] !== t19) {
    t20 = <div className="flex items-center gap-2">{t13}{t19}</div>;
    $[61] = t13;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== t12 || $[65] !== t20) {
    t21 = <div className="flex justify-between items-center w-full max-md:sticky max-md:bottom-0 max-md:bg-primary">{t12}{t20}</div>;
    $[64] = t12;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== t10 || $[68] !== t21) {
    t22 = <div className="flex flex-col gap-4 w-full p-1">{t10}{t21}</div>;
    $[67] = t10;
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  return t22;
};
function _temp($) {
  return $.runMode.invalidJson;
}
function _temp2($_0) {
  return $_0.testForm.playbookHasErrors;
}
function _temp3($_2) {
  return $_2.runMode.run.alreadyOnboardedHint;
}
function _temp4($_3) {
  return $_3.close;
}
function _temp5($_4) {
  return $_4.runMode.run.idempotencyWarning;
}
function _temp6($_5) {
  return $_5.runMode.run.runTest;
}